import { useForm } from "@mantine/form";
import {
    TextInput,
    PasswordInput,
    Text,
    Paper,
    PaperProps,
    Button,
    Anchor,
    Stack,
    CheckIcon,
    Alert,
    Divider,
    Group,
} from "@mantine/core";
import { register } from "./Authentication";
import { useMutation } from "react-query";
import { IconAlertCircle } from "@tabler/icons-react";
import { GoogleButton } from "./LoginForm";
import * as Sentry from "@sentry/react";
import { LoggedOutStorage, useLoggedOutStorage } from "./ZustandState";

export interface RegisterFormValues {
    username: string;
    email: string;
    password: string;
    confirmPassword: string;
}

export function RegisterForm(props: PaperProps) {
    const loggedOutData = useLoggedOutStorage((state) => state);
    const setLoggedOutData = useLoggedOutStorage((state) => state.setLoggedOutData);
    const attempts = [...loggedOutData.whiteAttempts, ...loggedOutData.blackAttempts];

    const form = useForm({
        initialValues: {
            username: "",
            email: "",
            password: "",
        },

        validate: {
            email: (val) => (/^\S+@\S+$/.test(val) ? null : "Invalid email"),
            password: (val) => (val.length < 6 ? "Password should include at least 6 characters" : null),
        },
    });

    const mutation = useMutation({
        mutationFn: (values: RegisterFormValues) => {
            return register(values.username, values.email, values.password, attempts).then(() => {
                setLoggedOutData({
                    ...loggedOutData,
                    whiteAttempts: [],
                    blackAttempts: [],
                    whiteRating: 1200,
                    blackRating: 1200,
                } as LoggedOutStorage);
            });
        },
    });

    const { isLoading, isSuccess, error } = mutation;

    let errorMessage = "";

    if (error) {
        console.log("error:", error);
        errorMessage = error?.response?.data?.message || "Registration failed";
        Sentry.captureException(error, {
            extra: {
                username: form.values.username,
                email: form.values.email,
                attempts: attempts,
                responseStatus: error?.response?.status,
                responseData: error?.response?.data,
            },
        });
        alert(error);
    }

    if (isSuccess) {
        return (
            <div className="flex flex-col justify-center items-center space-y-8">
                <CheckIcon className="w-32 mr-2" color="#2f9e44" fontSize="large" />
                <h1>Success!</h1>
                <div className="flex">
                    <a href="/login" className="cursor-pointer">
                        Back to login page
                    </a>
                </div>
            </div>
        );
    }

    return (
        <Paper radius="md" withBorder className="w-[30rem] p-12 max-w-[100vw] mt-[25%]" {...props}>
            <h1 className="mb-12">Create an account</h1>

            <Group grow mb="md" mt="md">
                <GoogleButton
                    onClick={async () =>
                        (window.location.href = `${import.meta.env.VITE_OPENING_TRAINER_BACKEND_URL}/auth/google-ot`)
                    }
                    radius="xl"
                >
                    Google
                </GoogleButton>
                {/* <TwitterButton radius="xl">Twitter</TwitterButton> */}
            </Group>
            <Divider label="Or continue with" labelPosition="center" my="lg" />

            {errorMessage && (
                // @ts-expect-error okk
                <Alert icon={<IconAlertCircle size="1rem" />} title={errorMessage} color="red" />
            )}

            <form
                onSubmit={form.onSubmit((values) => {
                    mutation.mutate(values);
                })}
            >
                <Stack>
                    <TextInput
                        required
                        withAsterisk={false}
                        label="Username"
                        value={form.values.username}
                        onChange={(event) => form.setFieldValue("username", event.currentTarget.value)}
                        error={form.errors.username && "Invalid username"}
                        radius="md"
                    />
                    <TextInput
                        required
                        withAsterisk={false}
                        label="Email"
                        value={form.values.email}
                        onChange={(event) => form.setFieldValue("email", event.currentTarget.value)}
                        error={form.errors.email && "Invalid email"}
                        radius="md"
                    />
                    <PasswordInput
                        required
                        withAsterisk={false}
                        label="Password"
                        value={form.values.password}
                        onChange={(event) => form.setFieldValue("password", event.currentTarget.value)}
                        error={form.errors.password && "Password should include at least 6 characters"}
                        radius="md"
                    />
                    <Button type="submit" fullWidth mt="xl" size="lg" loading={isLoading}>
                        Sign up
                    </Button>
                    <Text ta="center" mt="md">
                        Already have an account?{" "}
                        <Anchor<"a"> href="/login" weight={700}>
                            Login
                        </Anchor>
                    </Text>
                </Stack>
            </form>
        </Paper>
    );
}
